import type { BaseQueryApi } from "@reduxjs/toolkit/query";
import {
	type BaseQueryFn,
	createApi,
	type FetchArgs,
	type FetchBaseQueryError,
	fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../config/env";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import { logout, setCredentials, updateUser } from "../features/auth/auth.slice";
import {
	extractApiErrorMessage,
	FORCE_LOGOUT_MESSAGE_KEY,
	isForceLogoutAuthCode,
	isInsufficientPermissionCode,
	parseApiError,
} from "../features/auth/authApiErrors";
import { parseLoginResponse } from "../features/auth/auth.utils";
import { addToast } from "../features/toast/toast.slice";
import type { RootState } from "./store";

const apiBaseUrl = getApiBaseUrl();

const realBaseQuery = fetchBaseQuery({
	baseUrl: apiBaseUrl,
	credentials: "include",
	prepareHeaders: (headers, { getState }) => {
		const token = (getState() as RootState).auth.accessToken;
		if (token) {
			headers.set("authorization", `Bearer ${token}`);
		}
		return headers;
	},
});

let refreshPromise: Promise<{
	data?: unknown;
	error?: FetchBaseQueryError;
}> | null = null;

let meRefreshPromise: Promise<boolean> | null = null;

function resetRefreshPromise() {
	refreshPromise = null;
}

/**
 * Clears local session, best-effort server logout, then hard-redirects to login.
 * Message is stashed in sessionStorage so it survives the reload.
 */
async function forceLogout(
	api: BaseQueryApi,
	message: string,
	extraOptions: Record<string, unknown> = {},
) {
	resetRefreshPromise();
	meRefreshPromise = null;

	const authState = (api.getState() as RootState).auth;
	if (authState.accessToken) {
		try {
			await realBaseQuery(
				{ url: API_ENDPOINTS.auth.logout, method: "POST" },
				api,
				extraOptions,
			);
		} catch {
			// Best-effort: still clear local session if logout API fails.
		}
	}

	api.dispatch(logout());
	try {
		sessionStorage.setItem(FORCE_LOGOUT_MESSAGE_KEY, message);
	} catch {
		// ignore storage errors
	}
	if (typeof window !== "undefined") {
		window.location.assign("/login");
	}
}

async function attemptTokenRefresh(
	api: BaseQueryApi,
	extraOptions: Record<string, unknown>,
	refreshToken: string,
) {
	if (!refreshPromise) {
		refreshPromise = realBaseQuery(
			{
				url: API_ENDPOINTS.auth.refresh,
				method: "POST",
				body: { refreshToken },
			},
			api,
			extraOptions,
		) as Promise<{ data?: unknown; error?: FetchBaseQueryError }>;
	}

	const refreshResult = await refreshPromise;
	const refreshBody = refreshResult.data as Record<string, unknown> | undefined;
	const refreshData = refreshBody?.data as Record<string, unknown> | undefined;

	if (refreshData?.accessToken) {
		const successMessage =
			typeof refreshBody?.message === "string" && refreshBody.message.trim()
				? refreshBody.message.trim()
				: "Tokens refreshed successfully.";
		return {
			ok: true as const,
			accessToken: refreshData.accessToken as string,
			refreshToken:
				(refreshData.refreshToken as string | undefined) || refreshToken,
			successMessage,
		};
	}

	const refreshError = refreshResult.error;
	const refreshMessage =
		extractApiErrorMessage(refreshError) ??
		"Your session has expired. Please log in again.";

	return {
		ok: false as const,
		message: refreshMessage,
	};
}

/** Soft-refresh module access via GET /me after INSUFFICIENT_PERMISSION. */
async function refreshCurrentUserPermissions(
	api: BaseQueryApi,
	extraOptions: Record<string, unknown>,
): Promise<boolean> {
	if (!meRefreshPromise) {
		meRefreshPromise = (async () => {
			const meResult = await realBaseQuery(
				{ url: API_ENDPOINTS.auth.me, method: "GET" },
				api,
				extraOptions,
			);
			if (meResult.error) return false;
			const user = parseLoginResponse(meResult.data).user;
			if (!user.id) return false;
			api.dispatch(updateUser(user));
			return true;
		})().finally(() => {
			meRefreshPromise = null;
		});
	}
	return meRefreshPromise;
}

const baseQueryWithReauth: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	const result = await realBaseQuery(args, api, extraOptions);

	if (!result.error) {
		return result;
	}

	const { message: apiMessage, code } = parseApiError(result.error);
	const fallbackMessage =
		apiMessage ?? "You are not authorized to perform this action.";
	const status = result.error.status;
	const authState = (api.getState() as RootState).auth;
	const extras = extraOptions as Record<string, unknown>;

	if (status === 403 && isInsufficientPermissionCode(code)) {
		await refreshCurrentUserPermissions(api, extras);
		if (apiMessage) {
			api.dispatch(addToast({ type: "error", message: apiMessage }));
		}
		return result;
	}

	if (status === 403 && isForceLogoutAuthCode(code)) {
		await forceLogout(
			api,
			apiMessage ?? "Your account is not allowed to sign in.",
			extras,
		);
		return result;
	}

	if (status === 401) {
		if (isForceLogoutAuthCode(code)) {
			await forceLogout(
				api,
				apiMessage ?? "Your session is no longer valid. Please sign in again.",
				extras,
			);
			return result;
		}

		if (authState.refreshToken) {
			const refreshOutcome = await attemptTokenRefresh(
				api,
				extras,
				authState.refreshToken,
			);

			if (refreshOutcome.ok) {
				if (!authState.user || !authState.authProvider) {
					await forceLogout(
						api,
						"Your session is no longer valid. Please sign in again.",
						extras,
					);
					return result;
				}

				api.dispatch(
					setCredentials({
						accessToken: refreshOutcome.accessToken,
						refreshToken: refreshOutcome.refreshToken,
						user: authState.user,
						authProvider: authState.authProvider,
					}),
				);
				api.dispatch(
					addToast({
						type: "success",
						message: refreshOutcome.successMessage,
					}),
				);
				resetRefreshPromise();
				return await realBaseQuery(args, api, extraOptions);
			}

			resetRefreshPromise();
			await forceLogout(api, refreshOutcome.message, extras);
			return result;
		}

		api.dispatch(addToast({ type: "error", message: fallbackMessage }));
		return result;
	}

	const backendMessage = extractApiErrorMessage(result.error);
	if (backendMessage) {
		api.dispatch(
			addToast({
				type: "error",
				message: backendMessage,
			}),
		);
	}

	return result;
};

export const apiSlice = createApi({
	baseQuery: baseQueryWithReauth,
	tagTypes: [
		"User",
		"Auth",
		"Property",
		"Events",
		"EventTypes",
		"PropertyMarketing",
		"Role",
		"RoleModule",
		"SubAdmin",
		"FnBRestaurant",
		"Cabana",
		"BeneficiaryApproval",
		"AppUpdate",
		"StaticContent",
		"PropertyExploreScreen",
		"PropertyHomeScreen",
	],
	endpoints: () => ({}),
});
